import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
} from "runtypes";

import { DynamicValue, DynamicValueTypes } from "./DynamicValue.js";
import { NumberArray, StringArray } from "./parameterValueType.js";
import { getNormalEnum } from "./runtypeEnums.js";

export const ParameterInputTypeLiteral = Union(
  Literal("TEXT_INPUT"),
  Literal("NUMERIC_INPUT"),
  Literal("DROPDOWN"),
  Literal("TABLE"),
  Literal("SLIDER"),
  Literal("DATE"),
  Literal("BUTTON"),
  Literal("CHECKBOX"),
  Literal("MULTISELECT"),
  Literal("FILE_UPLOAD"),
);
export type ParameterInputType = Static<typeof ParameterInputTypeLiteral>;
export const ParameterInputType = getNormalEnum(ParameterInputTypeLiteral);

export const ParameterOutputTypeLiteral = Union(
  Literal("STRING"),
  Literal("NUMBER"),
  Literal("DATA_FRAME"),
  Literal("DATETIME"),
  Literal("BOOLEAN"),
  Literal("DYNAMIC"),
  Literal("LIST_STRING"),
  Literal("LIST_NUMBER"),
);
export type ParameterOutputType = Static<typeof ParameterOutputTypeLiteral>;
export const ParameterOutputType = getNormalEnum(ParameterOutputTypeLiteral);

export const KeyValueOption = RRecord({
  key: String,
  value: String,
});
export type KeyValueOption = Static<typeof KeyValueOption>;

export const DynamicOrStaticList = Union(
  StringArray,
  NumberArray,
  DynamicValueTypes,
  Array(KeyValueOption),
);
export type DynamicOrStaticList = Static<typeof DynamicOrStaticList>;

export const BooleanWithNumber = RRecord({
  enabled: Boolean,
  numberValue: Number.Or(Null),
});
export type BooleanWithNumber = Static<typeof BooleanWithNumber>;

export const NumberFormattingOptions = RRecord({
  format: Optional(
    Union(Literal("NUMBER"), Literal("PERCENTAGE"), Literal("CURRENCY")),
  ),
  thousands: Optional(Boolean),
  currencySymbol: Optional(String),
});
export type NumberFormattingOptions = Static<typeof NumberFormattingOptions>;

export const DropdownOptions = RRecord({
  valueOptions: DynamicOrStaticList,
  optional: Optional(Boolean),
});
export type DropdownOptions = Static<typeof DropdownOptions>;

export const TextOptions = RRecord({
  multiline: Boolean,
});
export type TextOptions = Static<typeof TextOptions>;

export const SliderOptions = RRecord({
  min: Number,
  max: Number,
  step: Number,
});
export type SliderOptions = Static<typeof SliderOptions>;

export const NumericOptions = RRecord({
  increment: Number,
  numberFormatting: Optional(NumberFormattingOptions),
});
export type NumericOptions = Static<typeof NumericOptions>;

export const DatetimeOptions = RRecord({
  enableTime: Boolean,
  showRelativeDates: Optional(Boolean),
  dynamicDefault: Optional(DynamicValue),
  useDateRange: Optional(Boolean),
  enableTimezonePicker: Optional(Boolean),
});
export type DatetimeOptions = Static<typeof DatetimeOptions>;

export const ButtonOptions = RRecord({
  intent: String,
  icon: String,
  text: String,
});
export type ButtonOptions = Static<typeof ButtonOptions>;

export const CheckboxOptions = RRecord({
  style: Literal("checkbox").Or(Literal("switch")),
  text: String,
});
export type CheckboxOptions = Static<typeof CheckboxOptions>;

export const TableOptions = RRecord({
  value: DynamicValue.Or(Null),
});
export type TableOptions = Static<typeof TableOptions>;

// Note: option types must all be distinct or it can lead to typeguard issues
export const MultiSelectOptions = RRecord({
  multiValueOptions: DynamicOrStaticList,
});
export type MultiSelectOptions = Static<typeof MultiSelectOptions>;

export const FileUploadOptions = RRecord({
  uploadType: Literal("csv").Or(Literal("excel")).Or(Literal("binary")),
});
export type FileUploadOptions = Static<typeof FileUploadOptions>;

export const ParameterOptions = Union(
  TextOptions,
  DropdownOptions,
  SliderOptions,
  NumericOptions,
  DatetimeOptions,
  ButtonOptions,
  CheckboxOptions,
  TableOptions,
  MultiSelectOptions,
  FileUploadOptions,
);
export type ParameterOptions = Static<typeof ParameterOptions>;

export const isDynamicDataType = (
  optionsWithDefaults: ParameterOptions,
): boolean => {
  return (
    (DropdownOptions.guard(optionsWithDefaults) &&
      DynamicValueTypes.guard(optionsWithDefaults.valueOptions)) ||
    (MultiSelectOptions.guard(optionsWithDefaults) &&
      DynamicValueTypes.guard(optionsWithDefaults.multiValueOptions)) ||
    (TableOptions.guard(optionsWithDefaults) &&
      DynamicValue.guard(optionsWithDefaults.value))
  );
};

export const ParameterDisplayOptionTypeLiteral = Union(
  Literal("STRING"),
  Literal("INT"),
  Literal("BOOLEAN_WITH_INT"),
  Literal("FLOAT"),
  Literal("LIST"),
  Literal("BOOLEAN"),
  Literal("STRING_SELECT"),
  Literal("DATAFRAME_SELECT"),
  Literal("DYNAMIC_DEFAULT"),
  Literal("NUMBER_FORMATTING"),
);
export type ParameterDisplayOptionType = Static<
  typeof ParameterDisplayOptionTypeLiteral
>;
export const ParameterDisplayOptionType = getNormalEnum(
  ParameterDisplayOptionTypeLiteral,
);

export const ParameterDisplayOptionSelectValue = RRecord({
  value: String,
  label: String,
});
export type ParameterDisplayOptionSelectValue = Static<
  typeof ParameterDisplayOptionSelectValue
>;

// Note this is not a runtype because runtypes do not support
// generic type parameters, let alone functions which use them.
export type ParameterDisplayOption<T> = {
  name: string;
  displayName: string;
  type: ParameterDisplayOptionType;
  tooltip?: string;
  allowedValues?: ParameterDisplayOptionSelectValue[];
  labelOnRight?: boolean;
  visibleIf?: (options: T) => boolean;
};

export type InputTypeToOption<T extends ParameterInputType> =
  T extends typeof ParameterInputType.DROPDOWN
    ? DropdownOptions
    : T extends typeof ParameterInputType.NUMERIC_INPUT
      ? NumericOptions
      : T extends typeof ParameterInputType.SLIDER
        ? SliderOptions
        : T extends typeof ParameterInputType.DATE
          ? DatetimeOptions
          : T extends typeof ParameterInputType.BUTTON
            ? ButtonOptions
            : T extends typeof ParameterInputType.CHECKBOX
              ? CheckboxOptions
              : T extends typeof ParameterInputType.TABLE
                ? TableOptions
                : T extends typeof ParameterInputType.MULTISELECT
                  ? MultiSelectOptions
                  : T extends typeof ParameterInputType.FILE_UPLOAD
                    ? FileUploadOptions
                    : T extends typeof ParameterInputType.TEXT_INPUT
                      ? TextOptions
                      : never;

interface ParameterInputTypeMetadata<T extends ParameterOptions> {
  allowedDataTypes: ParameterOutputType[];
  options?: ParameterDisplayOption<T>[];
  defaultOptions: T;
  defaultValue?: string | boolean | number | string[] | number[];
}

export const ParameterInputMetadata: {
  [key in ParameterInputType]: ParameterInputTypeMetadata<
    InputTypeToOption<key>
  >;
} = {
  [ParameterInputType.TEXT_INPUT]: {
    allowedDataTypes: [ParameterOutputType.STRING],
    options: [
      {
        name: "multiline",
        displayName: "Multiline",
        type: ParameterDisplayOptionType.BOOLEAN,
      },
    ],
    defaultOptions: {
      multiline: false,
    },
    defaultValue: "",
  },
  [ParameterInputType.NUMERIC_INPUT]: {
    allowedDataTypes: [ParameterOutputType.NUMBER],
    options: [
      {
        name: "increment",
        displayName: "Increment",
        type: ParameterDisplayOptionType.FLOAT,
      },
      {
        name: "numberFormatting",
        displayName: "Number Formatting",
        type: ParameterDisplayOptionType.NUMBER_FORMATTING,
      },
    ],
    defaultOptions: {
      // Note: Changing these default options will change
      // all existing numeric inputs (which have never had
      // their options changed), not just new ones.
      increment: 1,
      numberFormatting: {
        format: "NUMBER",
        thousands: false,
        currencySymbol: "$",
      },
    },
    defaultValue: 0,
  },
  [ParameterInputType.BUTTON]: {
    allowedDataTypes: [ParameterOutputType.BOOLEAN],
    options: [
      {
        name: "intent",
        displayName: "Style",
        type: ParameterDisplayOptionType.STRING_SELECT,
        allowedValues: [
          {
            label: "Default",
            value: "none",
          },
          {
            label: "Primary",
            value: "primary",
          },
          {
            label: "Success",
            value: "success",
          },
          {
            label: "Warning",
            value: "warning",
          },
          {
            label: "Danger",
            value: "danger",
          },
        ],
      },
      {
        name: "icon",
        displayName: "Icon",
        type: ParameterDisplayOptionType.STRING,
        tooltip:
          "List of available icons here: https://blueprintjs.com/docs/#icons/icons-list",
      },
      {
        name: "text",
        displayName: "Text",
        type: ParameterDisplayOptionType.STRING,
      },
    ],
    defaultOptions: {
      intent: "none",
      icon: "play",
      text: "Run",
    },
  },
  [ParameterInputType.CHECKBOX]: {
    allowedDataTypes: [ParameterOutputType.BOOLEAN],
    options: [
      {
        name: "style",
        displayName: "Style",
        type: ParameterDisplayOptionType.STRING_SELECT,
        allowedValues: [
          {
            label: "Checkbox",
            value: "checkbox",
          },
          {
            label: "Switch",
            value: "switch",
          },
        ],
      },
      {
        name: "text",
        displayName: "Text",
        type: ParameterDisplayOptionType.STRING,
      },
    ],
    defaultOptions: {
      style: "checkbox",
      text: "Checked",
    },
    defaultValue: false,
  },
  [ParameterInputType.DROPDOWN]: {
    allowedDataTypes: [ParameterOutputType.STRING, ParameterOutputType.NUMBER],
    options: [
      {
        name: "valueOptions",
        displayName: "Values",
        type: ParameterDisplayOptionType.LIST,
      },
      {
        name: "optional",
        displayName: "Allow users to clear selection",
        type: ParameterDisplayOptionType.BOOLEAN,
        tooltip: "Allows the dropdown value to be cleared",
      },
    ],
    defaultOptions: {
      valueOptions: { variableName: null },
      optional: false,
    },
  },
  [ParameterInputType.TABLE]: {
    allowedDataTypes: [ParameterOutputType.DATA_FRAME],
    options: [
      {
        name: "value",
        displayName: "Pre-load data",
        type: ParameterDisplayOptionType.DATAFRAME_SELECT,
        tooltip:
          "Start with an empty table or pre-load table values with a dataframe",
      },
    ],
    defaultOptions: {
      value: null,
    },
  },
  [ParameterInputType.SLIDER]: {
    allowedDataTypes: [ParameterOutputType.NUMBER],
    options: [
      {
        name: "min",
        displayName: "Min",
        type: ParameterDisplayOptionType.FLOAT,
      },
      {
        name: "max",
        displayName: "Max",
        type: ParameterDisplayOptionType.FLOAT,
      },
      {
        name: "step",
        displayName: "Step size",
        type: ParameterDisplayOptionType.FLOAT,
      },
    ],
    defaultOptions: {
      min: 0,
      max: 10,
      step: 1,
    },
    defaultValue: 0,
  },
  [ParameterInputType.DATE]: {
    allowedDataTypes: [ParameterOutputType.DATETIME],
    options: [
      {
        name: "dynamicDefault",
        displayName: "Use a variable as the default value",
        type: ParameterDisplayOptionType.DYNAMIC_DEFAULT,
      },
      {
        name: "enableTime",
        displayName: "Time",
        type: ParameterDisplayOptionType.BOOLEAN,
      },
      {
        name: "showRelativeDates",
        displayName: "Relative",
        type: ParameterDisplayOptionType.BOOLEAN,
      },
      {
        name: "testString",
        displayName: "Test string",
        type: ParameterDisplayOptionType.STRING,
      },
    ],
    defaultOptions: {
      enableTime: false,
      showRelativeDates: true,
      dynamicDefault: undefined,
      useDateRange: false,
    },
  },
  [ParameterInputType.MULTISELECT]: {
    allowedDataTypes: [
      ParameterOutputType.LIST_STRING,
      ParameterOutputType.LIST_NUMBER,
    ],
    options: [
      {
        name: "multiValueOptions",
        displayName: "Values",
        type: ParameterDisplayOptionType.LIST,
      },
    ],
    defaultOptions: {
      multiValueOptions: { variableName: null },
    },
    defaultValue: [],
  },
  [ParameterInputType.FILE_UPLOAD]: {
    allowedDataTypes: [ParameterOutputType.DATA_FRAME],
    options: [
      {
        name: "uploadType",
        displayName: "Type",
        type: ParameterDisplayOptionType.STRING_SELECT,
        allowedValues: [
          {
            label: "CSV",
            value: "csv",
          },
          {
            label: "Excel",
            value: "excel",
          },
          {
            label: "File (binary)",
            value: "binary",
          },
        ],
      },
    ],
    defaultOptions: {
      uploadType: "csv",
    },
  },
};

export const typeToHumanString = (
  typeString: string | null | undefined,
): string => {
  if (typeString == null || typeString.length === 0) {
    return "";
  }

  let cleaned = typeString.toLowerCase().replace("_", " ");

  // handle lists separately
  if (cleaned.startsWith("list")) {
    cleaned = cleaned.split(" ").join(" of ") + "s";
  }

  return cleaned.charAt(0).toUpperCase() + cleaned.substring(1);
};
